import { useMemo } from "react"
import { FilterableSelect } from "@/components/ui/filterable-select"
import type { FilterableSelectOption } from "@/components/ui/filterable-select"
import { ALL_COUNTRIES, COUNTRY_GROUPS } from "../../countryConstants"
import type { ExtractedField } from "../../types"

interface CountrySelectFieldProps {
  field: ExtractedField
  editableFieldAttr: Record<string, string>
  focusTargetAttr: Record<string, string>
  onSelectChange: (field: ExtractedField, value: string) => void | Promise<void>
  disabled?: boolean
}

// Build continent lookup: code -> continent name
const COUNTRY_CONTINENT_MAP: Record<string, string> = {}
for (const group of COUNTRY_GROUPS) {
  for (const country of group.countries) {
    COUNTRY_CONTINENT_MAP[country.code] = group.name
  }
}

const COUNTRY_OPTIONS: FilterableSelectOption[] = ALL_COUNTRIES.map((c) => ({
  value: c.code,
  label: `${c.code} - ${c.name}`,
  details: COUNTRY_CONTINENT_MAP[c.code]
}))

export const CountrySelectField = ({
  field,
  editableFieldAttr,
  focusTargetAttr,
  onSelectChange,
  disabled
}: CountrySelectFieldProps): JSX.Element => {
  const currentValue = field.field_value?.trim().toUpperCase() || ""

  const triggerExtraProps = useMemo(
    () => ({ ...editableFieldAttr, ...focusTargetAttr }),
    [editableFieldAttr, focusTargetAttr]
  )

  return (
    <FilterableSelect
      value={currentValue || "none"}
      options={COUNTRY_OPTIONS}
      onChange={(value) => {
        const effectiveValue = value === "none" ? "" : value
        onSelectChange(field, effectiveValue)
      }}
      disabled={disabled}
      placeholder="Select country"
      searchPlaceholder="Search countries..."
      showClearOption
      clearOptionLabel="No value"
      maxVisible={100}
      triggerClassName="h-9 text-sm font-semibold"
      triggerExtraProps={triggerExtraProps}
    />
  )
}
